import React, { useMemo, useState, useEffect } from 'react';
import { useApplicationStore } from '../../stores/applicationStore';
import { useAuthStore } from '../../stores/authStore';
import { Calendar, Plus, CalendarDays, ArrowLeft, ChevronLeft, ChevronRight, Clock, MapPin, User, Mail, Phone, Building2, FileText, RefreshCw } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { EditInterviewModal } from './EditInterviewModal';
import { useNavigate, useLocation } from 'react-router-dom';

function getMonthDays(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export const InterviewCalendar: React.FC = () => {
  const { applications, updateApplicationInFirestore, loadApplications } = useApplicationStore();
  const { user } = useAuthStore();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [form, setForm] = useState({
    applicationId: '',
    date: '',
    time: '',
    type: 'Online',
    location: '',
    notes: '',
    reminder: true,
    contact: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    contactRole: '',
  });
  
  // Stato per l'indirizzo automatico
  const [selectedApplicationAddress, setSelectedApplicationAddress] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();

  // Stato per la visibilità del calendario su mobile (dropdown behavior)
  const [showCalendar, setShowCalendar] = useState(true);

  // Controlla se aprire automaticamente il modal nuovo colloquio
  useEffect(() => {
    if (location.state?.openAddModal) {
      setShowAddModal(true);
      // Pulisci lo state per evitare di riaprire il modal
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Carica le candidature se non sono presenti
  useEffect(() => {
    if (user?.uid && applications.length === 0) {
      console.log('🔄 Caricamento candidature per calendario...');
      loadApplications(user.uid);
    }
  }, [user?.uid, applications.length, loadApplications]);

  // Aggiorna indirizzo quando cambia la candidatura selezionata
  useEffect(() => {
    if (form.applicationId) {
      const selectedApp = applications.find(app => app.id === form.applicationId);
      if (selectedApp?.companyDetails?.address) {
        setSelectedApplicationAddress(selectedApp.companyDetails.address);
      } else {
        setSelectedApplicationAddress('');
      }
    } else {
      setSelectedApplicationAddress('');
    }
  }, [form.applicationId, applications]);

  // Debug per vedere le candidature
  useEffect(() => {
    console.log('📋 Candidature disponibili nel calendario:', applications.length);
    applications.forEach(app => {
      console.log(`  - ${app.companyName} - ${app.position} (ID: ${app.id})`);
    });
  }, [applications]);

  // Estrai tutti i colloqui da tutte le candidature
  const interviews = useMemo(() => {
    return applications.flatMap(app =>
      (app.interviews || []).map(intv => ({
        ...intv,
        companyName: app.companyName,
        position: app.position,
        applicationId: app.id,
      }))
    );
  }, [applications]);

  // Statistiche
  const stats = {
    total: interviews.length,
    thisMonth: interviews.filter(intv => {
      const date = new Date(intv.date.seconds * 1000);
      return date.getFullYear() === year && date.getMonth() === month;
    }).length,
    today: interviews.filter(intv => {
      const date = new Date(intv.date.seconds * 1000);
      return date.getDate() === today.getDate() && 
             date.getMonth() === today.getMonth() && 
             date.getFullYear() === today.getFullYear();
    }).length,
    upcoming: interviews.filter(intv => {
      const date = new Date(intv.date.seconds * 1000);
      return date > today;
    }).length,
  };

  // Colloqui per giorno
  const interviewsByDay: Record<number, typeof interviews> = {};
  interviews.forEach(intv => {
    const date = new Date(intv.date.seconds * 1000);
    if (date.getFullYear() === year && date.getMonth() === month) {
      const day = date.getDate();
      if (!interviewsByDay[day]) interviewsByDay[day] = [];
      interviewsByDay[day].push(intv);
    }
  });

  const daysInMonth = getMonthDays(year, month);
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // Lunedì inizio settimana

  // Empty state
  const hasAny = interviews.some(intv => {
    const date = new Date(intv.date.seconds * 1000);
    return date.getFullYear() === year && date.getMonth() === month;
  });

  const handleRefresh = async () => {
    if (!user?.uid) return;
    setIsRefreshing(true);
    try {
      await loadApplications(user.uid);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Salva nuovo colloquio
  const handleAddInterview = async () => {
    console.log('🚀 Inizio handleAddInterview');
    console.log('📋 Form data:', form);
    console.log('👤 User ID:', user?.uid);
    console.log('📊 Applications count:', applications.length);
    
    if (!form.applicationId || !form.date || !form.time || !user?.uid) {
      console.error('❌ Dati mancanti:', {
        applicationId: form.applicationId,
        date: form.date,
        time: form.time,
        userId: user?.uid
      });
      return;
    }
    
    setSaving(true);
    try {
      const app = applications.find(a => a.id === form.applicationId);
      console.log('🔍 Candidatura trovata:', app);
      
      if (!app) {
        console.error('❌ Candidatura non trovata con ID:', form.applicationId);
        console.log('📋 Candidature disponibili:', applications.map(a => ({ id: a.id, company: a.companyName })));
        return;
      }
      
      const interviewDateTime = new Date(`${form.date}T${form.time}`);
      console.log('📅 Data colloquio:', interviewDateTime);
      
      const newInterview = {
        id: uuidv4(),
        date: Timestamp.fromDate(interviewDateTime),
        type: form.type as 'Presenza' | 'Online' | 'Telefono',
        location: form.location,
        notes: form.notes,
        reminder: form.reminder,
        ...(form.contact && { contact: form.contact }),
        ...(form.contactPerson && { contactPerson: form.contactPerson }),
        ...(form.contactEmail && { contactEmail: form.contactEmail }),
        ...(form.contactPhone && { contactPhone: form.contactPhone }),
        ...(form.contactRole && { contactRole: form.contactRole }),
      };
      
      console.log('📝 Nuovo colloquio da aggiungere:', newInterview);
      console.log('📊 Colloqui esistenti:', app.interviews || []);
      
      // Pulisci i colloqui esistenti da campi undefined
      const cleanExistingInterviews = (app.interviews || []).map(interview => {
        const cleanInterview = { ...interview } as any;
        Object.keys(cleanInterview).forEach(key => {
          if (cleanInterview[key] === undefined) {
            delete cleanInterview[key];
          }
        });
        return cleanInterview;
      });
      
      const updatedInterviews = [...cleanExistingInterviews, newInterview];
      console.log('📊 Colloqui aggiornati:', updatedInterviews);
      
      console.log('🔄 Chiamata updateApplicationInFirestore...');
      await updateApplicationInFirestore(user.uid, app.id, {
        interviews: updatedInterviews,
      });
      
      console.log('✅ Colloquio aggiunto con successo!');
      setShowAddModal(false);
      setForm({
        applicationId: '',
        date: '',
        time: '',
        type: 'Online',
        location: '',
        notes: '',
        reminder: true,
        contact: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        contactRole: '',
      });
    } catch (error) {
      console.error('❌ Errore nell\'aggiunta colloquio:', error);
    } finally {
      setSaving(false);
    }
  };

  // Gestisce l'apertura del modal di modifica
  const handleEditInterview = (interview: any) => {
    setSelectedInterview(interview);
    setShowEditModal(true);
  };

  // Gestisce la chiusura del modal di modifica
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedInterview(null);
  };

  // Gestisce l'aggiornamento del colloquio
  const handleInterviewUpdated = () => {
    // Ricarica le candidature per aggiornare il calendario
    if (user?.uid) {
      loadApplications(user.uid);
    }
  };

  const handleDayClick = (day: number) => {
    setSelectedDay(day);
    // Su mobile, nascondi il calendario quando si seleziona un giorno
    if (window.innerWidth < 1024) { // lg breakpoint
      setShowCalendar(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header principale - Mobile rimpicciolito */}
      <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-1.5 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
              aria-label="Torna alla dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-base font-bold text-blue-900">Calendario Colloqui</h1>
              <p className="text-xs text-gray-600">Gestisci tutti i tuoi appuntamenti di lavoro</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-1.5 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors disabled:opacity-50"
              aria-label="Aggiorna"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Nuovo Colloquio</span>
            </button>
          </div>
        </div>
      </div>

      {/* Header principale - Desktop */}
      <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors"
              aria-label="Torna alla dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-blue-900">Calendario Colloqui</h1>
              <p className="text-gray-600">Gestisci tutti i tuoi appuntamenti di lavoro</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors disabled:opacity-50"
              aria-label="Aggiorna"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              <span>Nuovo Colloquio</span>
            </button>
          </div>
        </div>
      </div>

      {/* Controlli Calendario - Solo Desktop */}
      <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="hidden lg:flex items-center gap-2 lg:gap-3">
            <CalendarDays className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-blue-500" />
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900">Calendario Colloqui</h2>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3">
            <button
              onClick={() => {
                if (month === 0) {
                  setMonth(11);
                  setYear(y => y - 1);
                } else {
                  setMonth(m => m - 1);
                }
                setSelectedDay(null);
              }}
              className="p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors"
              aria-label="Mese precedente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-semibold text-sm sm:text-lg lg:text-xl min-w-[100px] sm:min-w-[120px] lg:min-w-[140px] text-center text-gray-900">
              {new Date(year, month, 1).toLocaleString('it-IT', { month: 'long' })} {year}
            </span>
            <button
              onClick={() => {
                if (month === 11) {
                  setMonth(0);
                  setYear(y => y + 1);
                } else {
                  setMonth(m => m + 1);
                }
                setSelectedDay(null);
              }}
              className="p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors"
              aria-label="Mese successivo"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche - Mobile ottimizzato (PRIMA) */}
      <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
            <div className="text-blue-600 text-lg font-bold">{stats.total}</div>
            <div className="text-xs text-blue-700 font-medium">Totale</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 text-center">
            <div className="text-green-600 text-lg font-bold">{stats.thisMonth}</div>
            <div className="text-xs text-green-700 font-medium">Mese</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 text-center">
            <div className="text-orange-600 text-lg font-bold">{stats.today}</div>
            <div className="text-xs text-orange-700 font-medium">Oggi</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 text-center">
            <div className="text-purple-600 text-lg font-bold">{stats.upcoming}</div>
            <div className="text-xs text-purple-700 font-medium">Prossimi</div>
          </div>
        </div>
      </div>

      {/* Controlli Mese - Mobile (DOPO le statistiche) */}
      <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => {
              if (month === 0) {
                setMonth(11);
                setYear(y => y - 1);
              } else {
                setMonth(m => m - 1);
              }
              setSelectedDay(null);
            }}
            className="p-1.5 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
            aria-label="Mese precedente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-semibold text-sm min-w-[80px] text-center text-gray-900">
            {new Date(year, month, 1).toLocaleString('it-IT', { month: 'long' })} {year}
          </span>
          <button
            onClick={() => {
              if (month === 11) {
                setMonth(0);
                setYear(y => y + 1);
              } else {
                setMonth(m => m + 1);
              }
              setSelectedDay(null);
            }}
            className="p-1.5 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
            aria-label="Mese successivo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Layout Desktop: Calendario + Card Vuota */}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6 space-y-4 lg:space-y-0">
        {/* Calendario a sinistra */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          {/* Titolo nascosto su mobile */}
          <div className="hidden lg:flex items-center gap-2 lg:gap-3 mb-4">
            <CalendarDays className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-blue-500" />
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900">Calendario Colloqui</h2>
          </div>
          <div className="grid grid-cols-7 gap-1 lg:gap-2 text-xs lg:text-sm text-center font-semibold text-gray-500 mb-1 lg:mb-2">
            {weekDays.map(d => <div key={d} className="py-1 sm:py-2 lg:py-3">{d}</div>)}
          </div>
          {/* Griglia giorni */}
          <div className="grid grid-cols-7 gap-1 lg:gap-2 bg-gray-100 rounded-xl p-1 sm:p-2 lg:p-4">
            {Array.from({ length: firstDayOfWeek }, (_, i) => <div key={i}></div>)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              const hasInterview = !!interviewsByDay[day];
              return (
                <button
                  key={day}
                  className={`h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 xl:h-16 xl:w-16 flex flex-col items-center justify-center rounded-lg border text-xs sm:text-sm lg:text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all hover:scale-105
                    ${hasInterview ? 'bg-blue-100 border-blue-400 text-blue-900 font-bold' : 'bg-white border-gray-200 text-gray-700'}
                    ${selectedDay === day ? 'ring-2 ring-blue-500' : ''}
                    ${isToday ? 'border-2 border-blue-600' : ''}
                    active:scale-95`}
                  onClick={() => handleDayClick(day)}
                  aria-label={`Giorno ${day}${hasInterview ? ', colloqui presenti' : ''}${isToday ? ', oggi' : ''}`}
                >
                  {day}
                  {hasInterview && (
                    <span className="mt-0.5 sm:mt-1 text-xs lg:text-sm text-blue-700 font-bold">{interviewsByDay[day].length} 📅</span>
                  )}
                  {isToday && <span className="block w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-blue-600 rounded-full mt-0.5"></span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Card dettagli a destra (solo desktop) */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-100 rounded-xl">
              <Calendar className="w-5 h-5 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Dettagli Giorno</h3>
          </div>
          
          {selectedDay && interviewsByDay[selectedDay] ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-blue-800 text-lg">
                  Colloqui del {selectedDay}/{month+1}/{year}
                </h4>
                <span className="text-sm text-gray-500">
                  {interviewsByDay[selectedDay].length} colloquio{interviewsByDay[selectedDay].length > 1 ? 'i' : ''}
                </span>
              </div>
              <div className="space-y-3">
                {interviewsByDay[selectedDay].map(intv => (
                  <button
                    key={intv.id}
                    className="w-full bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-lg hover:border-blue-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200"
                    onClick={() => handleEditInterview(intv)}
                    aria-label={`Modifica colloquio per ${intv.position} presso ${intv.companyName}`}
                  >
                    {/* Header con posizione e azienda */}
                    <div className="mb-3">
                      <h5 className="font-bold text-gray-900 text-base mb-1 truncate">
                        {intv.position}
                      </h5>
                      <p className="text-sm text-gray-600 font-medium truncate mb-2">
                        {intv.companyName}
                      </p>
                      {/* Badge tipo colloquio */}
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        intv.type === 'Presenza' ? 'bg-blue-100 text-blue-800' :
                        intv.type === 'Online' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        <span className="mr-1">{intv.type === 'Presenza' ? '🏢' : intv.type === 'Online' ? '💻' : '📱'}</span>
                        <span>{intv.type}</span>
                      </div>
                    </div>

                    {/* Informazioni principali */}
                    <div className="space-y-2 mb-3">
                      {/* Orario */}
                      <div className="flex items-center text-sm text-gray-700">
                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="font-medium">
                          {new Date(intv.date.seconds * 1000).toLocaleTimeString('it-IT', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>

                      {/* Luogo/Link */}
                      {intv.location && (
                        <div className="flex items-start text-sm text-gray-700">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                          <span className="flex-1 break-words">{intv.location}</span>
                        </div>
                      )}

                      {/* Contatto */}
                      {intv.contact && (
                        <div className="flex items-center text-sm text-gray-700">
                          <User className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="truncate">{intv.contact}</span>
                        </div>
                      )}

                      {/* Informazioni di Contatto Dettagliate */}
                      {(intv.contactPerson || intv.contactEmail || intv.contactPhone || intv.contactRole) && (
                        <div className="space-y-1 mt-2 p-2 bg-white rounded-lg border">
                          {intv.contactPerson && (
                            <div className="flex items-center text-xs text-gray-600">
                              <User className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="font-medium truncate">{intv.contactPerson}</span>
                              {intv.contactRole && <span className="ml-1 text-gray-500 truncate">({intv.contactRole})</span>}
                            </div>
                          )}
                          {intv.contactEmail && (
                            <div className="flex items-center text-xs text-gray-600">
                              <Mail className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="truncate">{intv.contactEmail}</span>
                            </div>
                          )}
                          {intv.contactPhone && (
                            <div className="flex items-center text-xs text-gray-600">
                              <Phone className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="truncate">{intv.contactPhone}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Note */}
                    {intv.notes && (
                      <div className="mb-3 p-3 bg-white rounded-lg border">
                        <div className="flex items-start text-sm text-gray-600">
                          <FileText className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                          <span className="flex-1 break-words italic">{intv.notes}</span>
                        </div>
                      </div>
                    )}

                    {/* Footer con reminder */}
                    {intv.reminder && (
                      <div className="flex items-center justify-end pt-2 border-t border-gray-100">
                        <span className="flex items-center text-xs text-green-600 font-medium">
                          <span className="mr-1">🔔</span>
                          Reminder attivo
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : selectedDay ? (
            <div className="text-center text-gray-500 py-8">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm">Nessun colloquio programmato per questo giorno</p>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm">Seleziona un giorno dal calendario per vedere i dettagli dei colloqui</p>
            </div>
          )}
        </div>
      </div>

      {/* Layout Mobile: Calendario o Dettagli (dropdown behavior) */}
      <div className="lg:hidden space-y-2">
        {/* Calendario (visibile quando showCalendar è true) */}
        {showCalendar && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6">
            {/* Titolo nascosto su mobile */}
            <div className="hidden lg:flex items-center gap-2 lg:gap-3 mb-4">
              <CalendarDays className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-blue-500" />
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900">Calendario Colloqui</h2>
            </div>
            <div className="grid grid-cols-7 gap-1 lg:gap-2 text-xs lg:text-sm text-center font-semibold text-gray-500 mb-1 lg:mb-2">
              {weekDays.map(d => <div key={d} className="py-1 sm:py-2 lg:py-3">{d}</div>)}
            </div>
            {/* Griglia giorni */}
            <div className="grid grid-cols-7 gap-1 lg:gap-2 bg-gray-100 rounded-xl p-1 sm:p-2 lg:p-4">
              {Array.from({ length: firstDayOfWeek }, (_, i) => <div key={i}></div>)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                const hasInterview = !!interviewsByDay[day];
                return (
                  <button
                    key={day}
                    className={`h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 xl:h-16 xl:w-16 flex flex-col items-center justify-center rounded-lg border text-xs sm:text-sm lg:text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all hover:scale-105
                      ${hasInterview ? 'bg-blue-100 border-blue-400 text-blue-900 font-bold' : 'bg-white border-gray-200 text-gray-700'}
                      ${selectedDay === day ? 'ring-2 ring-blue-500' : ''}
                      ${isToday ? 'border-2 border-blue-600' : ''}
                      active:scale-95`}
                    onClick={() => handleDayClick(day)}
                    aria-label={`Giorno ${day}${hasInterview ? ', colloqui presenti' : ''}${isToday ? ', oggi' : ''}`}
                  >
                    {day}
                    {hasInterview && (
                      <span className="mt-0.5 sm:mt-1 text-xs lg:text-sm text-blue-700 font-bold">{interviewsByDay[day].length} 📅</span>
                    )}
                    {isToday && <span className="block w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-blue-600 rounded-full mt-0.5"></span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Dettagli colloqui (visibili quando showCalendar è false) */}
        {!showCalendar && (
          <div className="space-y-2">
            {/* Header per tornare al calendario */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowCalendar(true)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Torna al calendario</span>
                </button>
                <span className="text-sm text-gray-500">
                  {selectedDay}/{month+1}/{year}
                </span>
              </div>
            </div>

            {/* Dettagli colloqui */}
            {selectedDay && interviewsByDay[selectedDay] ? (
              <div className="bg-white border border-blue-200 rounded-2xl shadow-sm p-2 animate-fade-in-up">
                <h3 className="font-semibold text-blue-800 mb-3 text-lg flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-blue-400" />
                  Colloqui del {selectedDay}/{month+1}/{year}
                </h3>
                <div className="space-y-2">
                  {interviewsByDay[selectedDay].map(intv => (
                    <button
                      key={intv.id}
                      className="w-full bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-lg hover:border-blue-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200"
                      onClick={() => handleEditInterview(intv)}
                      aria-label={`Modifica colloquio per ${intv.position} presso ${intv.companyName}`}
                    >
                      {/* Header con posizione e azienda */}
                      <div className="mb-3">
                        <h4 className="font-bold text-gray-900 text-base mb-1 truncate">
                          {intv.position}
                        </h4>
                        <p className="text-sm text-gray-600 font-medium truncate mb-2">
                          {intv.companyName}
                        </p>
                        {/* Badge tipo colloquio */}
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          intv.type === 'Presenza' ? 'bg-blue-100 text-blue-800' :
                          intv.type === 'Online' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          <span className="mr-1">{intv.type === 'Presenza' ? '🏢' : intv.type === 'Online' ? '💻' : '📱'}</span>
                          <span>{intv.type}</span>
                        </div>
                      </div>

                      {/* Informazioni principali */}
                      <div className="space-y-2 mb-3">
                        {/* Orario */}
                        <div className="flex items-center text-sm text-gray-700">
                          <Clock className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium">
                            {new Date(intv.date.seconds * 1000).toLocaleTimeString('it-IT', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>

                        {/* Luogo/Link */}
                        {intv.location && (
                          <div className="flex items-start text-sm text-gray-700">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                            <span className="flex-1 break-words">{intv.location}</span>
                          </div>
                        )}

                        {/* Contatto */}
                        {intv.contact && (
                          <div className="flex items-center text-sm text-gray-700">
                            <User className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="truncate">{intv.contact}</span>
                          </div>
                        )}

                        {/* Informazioni di Contatto Dettagliate */}
                        {(intv.contactPerson || intv.contactEmail || intv.contactPhone || intv.contactRole) && (
                          <div className="space-y-1 mt-2 p-2 bg-white rounded-lg border">
                            {intv.contactPerson && (
                              <div className="flex items-center text-xs text-gray-600">
                                <User className="w-3 h-3 mr-1 text-gray-400" />
                                <span className="font-medium truncate">{intv.contactPerson}</span>
                                {intv.contactRole && <span className="ml-1 text-gray-500 truncate">({intv.contactRole})</span>}
                              </div>
                            )}
                            {intv.contactEmail && (
                              <div className="flex items-center text-xs text-gray-600">
                                <Mail className="w-3 h-3 mr-1 text-gray-400" />
                                <span className="truncate">{intv.contactEmail}</span>
                              </div>
                            )}
                            {intv.contactPhone && (
                              <div className="flex items-center text-xs text-gray-600">
                                <Phone className="w-3 h-3 mr-1 text-gray-400" />
                                <span className="truncate">{intv.contactPhone}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Note */}
                      {intv.notes && (
                        <div className="mb-3 p-3 bg-white rounded-lg border">
                          <div className="flex items-start text-sm text-gray-600">
                            <FileText className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                            <span className="flex-1 break-words italic">{intv.notes}</span>
                          </div>
                        </div>
                      )}

                      {/* Footer con reminder */}
                      {intv.reminder && (
                        <div className="flex items-center justify-end pt-2 border-t border-gray-100">
                          <span className="flex items-center text-xs text-green-600 font-medium">
                            <span className="mr-1">🔔</span>
                            Reminder attivo
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : selectedDay ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
                <div className="text-sm text-gray-500 text-center py-4">Nessun colloquio programmato per questo giorno.</div>
              </div>
            ) : null}
          </div>
        )}

        {/* Empty state quando non ci sono colloqui (solo quando showCalendar è true) */}
        {showCalendar && !hasAny && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 lg:p-12">
            <div className="flex flex-col items-center justify-center text-center text-gray-400">
              <CalendarDays className="w-16 h-16 lg:w-20 lg:h-20 mb-2 lg:mb-4" />
              <div className="text-lg lg:text-xl font-bold mb-2 lg:mb-3 text-gray-600">Nessun colloquio programmato</div>
              <div className="text-gray-500 mb-4 lg:mb-6 text-sm lg:text-base">Aggiungi subito un colloquio per visualizzarlo qui!</div>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                <span>Primo Colloquio</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal aggiunta colloquio */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col sm:rounded-2xl sm:shadow-2xl sm:max-w-md sm:max-w-lg md:max-w-xl sm:h-auto sm:mx-auto sm:my-4">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Aggiungi Appuntamento/Colloquio
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Programma un nuovo colloquio
                </p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 pb-40">
            <div className="space-y-6">
              {/* Candidatura */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Candidatura
                </label>
                <select
                  value={form.applicationId}
                  onChange={e => setForm(f => ({ ...f, applicationId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Seleziona candidatura...</option>
                  {applications.length > 0 ? (
                    applications.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.companyName} - {app.position}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Nessuna candidatura disponibile</option>
                  )}
                </select>
                {applications.length === 0 && (
                  <p className="text-sm text-orange-600 mt-1">
                    ⚠️ Crea prima una candidatura per poter aggiungere un colloquio
                  </p>
                )}
              </div>
              
              {/* Indirizzo automatico della sede */}
              {selectedApplicationAddress && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Indirizzo sede
                  </label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 text-sm">
                    {selectedApplicationAddress}
                  </div>
                  <p className="text-xs text-gray-500">
                    Indirizzo automatico dalla candidatura selezionata
                  </p>
                </div>
              )}
              
              {/* Data e Ora */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Data
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Ora
                  </label>
                  <input
                    type="time"
                    value={form.time}
                    onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
              
              {/* Tipo */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Tipo Colloquio
                </label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="Presenza">Presenza</option>
                  <option value="Online">Online</option>
                  <option value="Telefono">Telefono</option>
                </select>
              </div>
              
              {/* Luogo/Link */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Luogo/Link
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Indirizzo, link meeting, ecc."
                />
              </div>

              {/* Contatto */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Contatto
                </label>
                <input
                  type="text"
                  value={form.contact}
                  onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Nome del referente, email, telefono..."
                />
              </div>

              {/* Informazioni di Contatto Dettagliate */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2">
                  Informazioni di Contatto
                </h4>
                
                {/* Persona di Riferimento */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Persona di Riferimento
                  </label>
                  <input
                    type="text"
                    value={form.contactPerson}
                    onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Nome e cognome..."
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="email@azienda.com"
                  />
                </div>

                {/* Telefono */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Numero di Telefono
                  </label>
                  <input
                    type="tel"
                    value={form.contactPhone}
                    onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="+41 79 000 12 12"
                  />
                </div>

                {/* Ruolo */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Ruolo
                  </label>
                  <input
                    type="text"
                    value={form.contactRole}
                    onChange={e => setForm(f => ({ ...f, contactRole: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="HR Manager, Recruiter, ecc."
                  />
                </div>
              </div>
              
              {/* Note */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Note
                </label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  placeholder="Note aggiuntive..."
                />
              </div>
              
              {/* Checkbox Reminder */}
              <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                <input
                  type="checkbox"
                  id="reminder"
                  checked={form.reminder}
                  onChange={e => setForm(f => ({ ...f, reminder: e.target.checked }))}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all"
                />
                <label htmlFor="reminder" className="ml-3 block text-sm font-medium text-gray-900">
                  Attiva reminder per questo colloquio
                </label>
              </div>
            </div>
          </div>
          
          {/* Footer - Fisso in basso con molto spazio sopra la footer bar */}
          <div className="p-4 pb-16 sm:pb-28 border-t border-gray-200 bg-white">
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-3 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm"
              >
                Annulla
              </button>
              <button
                onClick={handleAddInterview}
                disabled={saving || !form.applicationId || !form.date || !form.time}
                className="flex-1 px-3 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-lg"
              >
                {saving ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Aggiungendo...
                  </span>
                ) : (
                  'Aggiungi'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal di modifica colloquio */}
      {showEditModal && selectedInterview && (
        <EditInterviewModal
          interview={selectedInterview}
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onInterviewUpdated={handleInterviewUpdated}
        />
      )}

      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s cubic-bezier(.4,0,.2,1);
        }
      `}</style>
    </div>
  );
}; 